import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  HeaderInput,
  HeaderTemplate,
  SectionHeader,
  SingleSelectionRadio,
  TABLE_INITIAL_STATE,
  TitleHeader,
  useSnackbarHandler,
  useDataTable, tomorrowDate,dayjsToPayloadFormat
} from 'web-library';
import dayjs, {Dayjs} from 'dayjs';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import RouteKey from '../../../routes/key';
import UserAdminAccountsDataTable from './UserAdminAccountsDataTable';
import SetAccountExpiryDateModal from './SetAccountExpiryDateModal';
import {
  PutSetAccountExpiryDateRequest,
  useSetAccountExpiryDate,
} from '../../../api-refactored/user-group-access-configuration/usePutSetAccountExpiryDate';
import { useGetCreateAuthorityAccessOptions } from '../../../api-refactored/account-management/useGetCreateAuthorityAccessOptions';

const { admin } = RouteKey.child;
const { adminAccountManagement, userAccountManagement } = RouteKey.admin.child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      marginBottom: theme.spacing(3),
    },
  },
  button: {
    height: 'fit-content',
    marginLeft: theme.spacing(3),
  },
}));

export enum AccountTypeMode {
  Candidate = 'Candidate',
  Coordinator = 'Coordinator',
}

interface UserAccountManagementListingProps {
  isOpsAdmin: boolean;
}

const UserAccountManagementListing = ({ isOpsAdmin }: UserAccountManagementListingProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const theme = useTheme();

  const dataTableHooks = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'createdAt',
    order: 'desc',
    size: 5,
    page: 0,
    search: '',
    filters: {},
  });

  const history = useHistory();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { state } = useLocation<{
    selectedRequestTypeMode: AccountTypeMode;
    action?: string;
    email?: string;
  }>();

  const { snackbarHandler } = useSnackbarHandler();

  const [selectedRequestTypeMode, setSelectedRequestTypeMode] = useState<AccountTypeMode>(
    state?.selectedRequestTypeMode || AccountTypeMode.Candidate
  );

  const [isNoAccess, setIsNoAccess] = useState<boolean>(false);

  const { data = [] } = useGetUserAuthorities();

  const selectedAccountUuids = Object.values(dataTableHooks.state.rowsSelected).map(
    (userAccounts: any) => userAccounts.uuid
  );

  const [openSetAccountExpiryDateModal, setOpenSetAccountExpiryDateModal] = useState<boolean>(
    false
  );
  const [openConfirmationModal, setOpenConfirmationModal] = useState<boolean>(false);
  const [expiryDate, setExpiryDate] = useState<Dayjs>(dayjs(tomorrowDate));
  const [refreshTable, setRefreshTable] = useState<number>(0);

  const getAccountType = () => {
    if (isOpsAdmin) return "ADMINISTRATOR";

    if (selectedRequestTypeMode === AccountTypeMode.Candidate) {
      return "CANDIDATES";
    } if (selectedRequestTypeMode === AccountTypeMode.Coordinator) {
      return "COORDINATORS";
    }

    return "";
  };

  const { data: hasCreatePermission } = useGetCreateAuthorityAccessOptions({
    targetAccountType: getAccountType()
  });

  const handleCreateAccount = () => {
    if (selectedRequestTypeMode === AccountTypeMode.Candidate) {
      history.push(`/${admin}/${userAccountManagement.route}/candidate`);
    } else if (selectedRequestTypeMode === AccountTypeMode.Coordinator) {
      history.push(`/${admin}/${userAccountManagement.route}/coordinator`);
    }
    if (isOpsAdmin) {
      history.push(`/${admin}/${adminAccountManagement.route}/admin`);
    }
  };

  const handleUpdateAccountExpiryDate = () => setOpenSetAccountExpiryDateModal(true);

  const onUpdateDialogClose = () => {
    setOpenSetAccountExpiryDateModal(false);
    setOpenConfirmationModal(false);
    setRefreshTable(refreshTable + 1);
  };

  const setAccountExpiryDate = useSetAccountExpiryDate({
    onSuccess: () => {
      onUpdateDialogClose();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.SetAccountExpiryDateSuccess', {
          expiryDate: dayjs(expiryDate).format('DD MMM YYYY'),
        }),
      });
      setExpiryDate(dayjs(tomorrowDate));
    },
    onError: () => {
      onUpdateDialogClose();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('admin->>UserAccountManagement.SetAccountExpiryDateFail', {
          expiryDate: dayjs(expiryDate).format('DD MMM YYYY'),
        }),
      });
      setExpiryDate(dayjs(tomorrowDate));
    },
  });

  const confirmSetAccountExpiryDateDialog = () => {
    setAccountExpiryDate.mutate({
      userUuids: selectedAccountUuids,
      expiryDate: dayjsToPayloadFormat(expiryDate),
    } as PutSetAccountExpiryDateRequest);
  };

  const displayCandidateOrCoordinatorTitle =
    selectedRequestTypeMode === AccountTypeMode.Candidate
      ? t('admin->>UserAccountManagement.CandidateAccounts')
      : t('admin->>UserAccountManagement.CoordinatorAccounts');

  useEffect(() => {
    if (state?.action === 'Reset password') {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.PasswordSentMessage', {
          email: state?.email,
        }),
      });
      history.replace({ state: {} });
    }
    if (state?.action === 'Re-send activation email') {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.ResendActivationEmailMessage', {
          email: state?.email,
        }),
      });
      history.replace({ state: {} });
    }
    if (
      state?.action === 'Block account' ||
      state?.action === 'Unblock account' ||
      state?.action === 'Unlock account'
    ) {
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>UserAccountManagement.ActionRequestMessage', {
          action: state?.action,
        }),
      });
      history.replace({ state: {} });
    }
  }, [history, selectedRequestTypeMode, snackbarHandler, state?.action, state?.email, t]);

  return (
    <>
      <TitleHeader
        title={
          isOpsAdmin
            ? t('admin->>UserAccountManagement.AdminAccountManagement')
            : t('admin->>UserAccountManagement.UserAccountManagement')
        }
      />
      {!isOpsAdmin && (
        <HeaderTemplate>
          <HeaderInput
            label={t('admin->>UserGroups.AccountType')}
            input={
              <SingleSelectionRadio
                data={[
                  {
                    label: t('admin->>UserGroups.Candidate'),
                    value: AccountTypeMode.Candidate,
                  },
                  {
                    label: t('admin->>UserGroups.Coordinator'),
                    value: AccountTypeMode.Coordinator,
                  },
                ]}
                onChange={(event) => {
                  setSelectedRequestTypeMode(event.target.value as AccountTypeMode);
                  setIsNoAccess(false);
                }}
                value={selectedRequestTypeMode}
              />
            }
            topBottom={false}
          />
        </HeaderTemplate>
      )}
      <ContentTemplate type="full">
        <Box className={classes.headerContainer}>
          {isOpsAdmin ? (
            <SectionHeader title={t('admin->>UserAccountManagement.AdminAccounts')} />
          ) : (
            <SectionHeader title={displayCandidateOrCoordinatorTitle} />
          )}
          <Box>
            {Object.keys(dataTableHooks.state.rowsSelected).length !== 0 && (
              <Button
                className={classes.button}
                fullWidth={isMobile}
                onClick={handleUpdateAccountExpiryDate}
                id="button-set-account-expiration-date"
              >
                {t('admin->>UserAccountManagement.SetAccountExpirationDate')}
              </Button>
            )}
            {hasCreatePermission?.showCreateButton && (
              <Button
                className={classes.button}
                fullWidth={isMobile}
                onClick={handleCreateAccount}
                id="button-create-account"
              >
                {t('admin->>UserAccountManagement.CreateAccount')}
              </Button>
            )}
          </Box>
        </Box>
        <UserAdminAccountsDataTable
          selectedRequestTypeMode={selectedRequestTypeMode}
          isOpsAdmin={isOpsAdmin}
          isNoAccess={isNoAccess}
          authorities={data}
          setIsNoAccess={setIsNoAccess}
          dataTableHooks={dataTableHooks}
          refreshTable={refreshTable}
        />
      </ContentTemplate>
      <SetAccountExpiryDateModal
        modalState={openSetAccountExpiryDateModal}
        setOpenModalState={setOpenSetAccountExpiryDateModal}
        openConfirmationModalState={openConfirmationModal}
        setOpenConfirmationModalState={setOpenConfirmationModal}
        expiryDate={expiryDate}
        setExpiryDate={setExpiryDate}
        onDialogConfirm={confirmSetAccountExpiryDateDialog}
      />
    </>
  );
};

export default UserAccountManagementListing;
